import React, { useState, useEffect } from 'react';

import styled from 'styled-components';
import { GetPageUsabilityClickRateResponseType } from '../../../../api/report/type';
import DefaultLink from '../../../Atoms/Link';
import { P3 } from '../../../Atoms/Typo';
import Header from '../../../Molecule/Card/CardHeader';
import ReportSummary from '../../../Molecule/Card/CardSummary/ReportSummary';
import Title from '../../../Molecule/Card/CardTitle/ReportTitle';
import DounutChart from '../../../Molecule/Chart/DonutChart/Simple';
import Spinner from '../../../Molecule/Spinner';

const Component = styled.div`
  width: 100%;
  height: 100%;
  min-height: 806px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: white;
  border: solid 1px ${(props) => props.theme.colors.grey200};
  border-radius: 8px;

  padding: 24px 24px 80px 24px;
`;

const SubReport = styled.div`
  ${P3};
  color: ${(props) => props.theme.colors.grey600};
  margin-top: 8px;
`;

const LinkWrapper = styled.div`
  width: fit-content;

  a {
    ${P3};
    color: ${(props) => props.theme.colors.grey500};

    :hover {
      color: ${(props) => props.theme.colors.grey500};
    }
  }
  margin-top: 24px;
`;

const Underline = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) => props.theme.colors.grey500};
  margin-top: 4px;
`;

const Body = styled.div`
  width: 100%;
  height: 356px;
`;

interface PageUsabilityReportCardProps {
  month: string;
  data: GetPageUsabilityClickRateResponseType['payload'] | null;
  isLoading: boolean;
}

interface PageUsabilityReportCardState {
  chartData: {
    group: string;
    rate: number;
  }[];
}

const PageUsabilityClickRateReportCard = ({
  month,
  data,
  isLoading,
}: PageUsabilityReportCardProps) => {
  const [allSiterate, setAllSiteRate] = useState(0);
  const [report, setReport] = useState<React.ReactNode>();
  const [chartData, setChartData] = useState<
    PageUsabilityReportCardState['chartData']
  >([]);

  useEffect(() => {
    if (data) {
      setAllSiteRate(data.all_page_use_per);
      const chart_data: PageUsabilityReportCardState['chartData'] = [];

      chart_data.push({
        group: `${month} 평균 유효 클릭 비율`,
        rate: data.page_use_per,
      });
      chart_data.push({
        group: `${month} 평균 무효 클릭 비율`,
        rate: 100 - data.page_use_per,
      });

      setChartData(chart_data);

      if (data.page_use_per >= 60) {
        setReport(
          <>
            {month} 한 달동안 평균 유효 클릭 비율은{' '}
            <em>{data.page_use_per}%</em>로<br />
            평균 유효 클릭 비율이 높습니다.
            <br /> 사용자가 클릭가능한 부분을 헷갈리지 않고 사용하고 있습니다.
          </>
        );
      } else if (data.page_use_per >= 30) {
        setReport(
          <>
            {month} 한 달동안 평균 유효 클릭 비율은{' '}
            <em>{data.page_use_per}%</em>로<br />
            대부분의 웹 사이트는 유효 클릭 비율이 {data.all_page_use_per}%
            이상입니다.
            <br />
            사용자가 클릭 영역을 구별하기 어렵습니다.
          </>
        );
      } else {
        setReport(
          <>
            {month} 한 달동안 평균 유효 클릭 비율은{' '}
            <em>{data.page_use_per}%</em>로<br />
            평균 유효 클릭 비율이 낮습니다.
            <br /> 페이지 UX분석에서 원인을 찾아보세요.
          </>
        );
      }
    }
  }, [data]);

  return (
    <Component>
      <Header>
        <div>
          <Title title="평균 유효 클릭 비율" />
          <ReportSummary>{report}</ReportSummary>
          <SubReport>
            대부분의 웹 사이트는 유효 클릭 비율이 {allSiterate}% 이상입니다.
          </SubReport>
          <LinkWrapper>
            <DefaultLink to="/pageAnalytics/uxAnalysisList">
              페이지 UX 분석으로 이동
            </DefaultLink>
            <Underline />
          </LinkWrapper>
        </div>
      </Header>

      <Body>{isLoading ? <Spinner /> : <DounutChart data={chartData} />}</Body>
    </Component>
  );
};

export default PageUsabilityClickRateReportCard;
